import { HistoryOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Descriptions as AntdDescriptions,
  Drawer,
  Input,
  Row,
  Segmented,
  Select,
  Space,
  Tooltip,
  Typography,
} from "antd";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomerSatisfactionStatus,
  getCustomerSatisfactionStatusColor,
  getCustomerSatisfactionStatusLabel,
} from "../../../models/customer-satisfaction/enum";
import {
  ICustomerDissatisfactionReason,
  ICustomerSatisfactionAudit,
  IOrderCustomerSatisfaction,
} from "../../../models/customer-satisfaction/response";
import { IUpdateOrderCustomerSatisfactionRequest } from "../../../models/customer-satisfaction/request";
import {
  mapCustomerDissatisfactionReason,
  mapCustomerSatisfactionAudit,
  normalizeListingResponse,
} from "../../../models/customer-satisfaction/mapper";
import EndPoints from "../../../services/end-points";
import {
  errorNotification,
  successNotification,
} from "../../../utils/helpers/notification";
import Descriptions from "../../general/antd/descriptions";
import Tag from "../../general/antd/tag";
import AuditChangesList from "../../customer-satisfaction/audit-changes-list";
import OperationalSignalTags from "../../customer-satisfaction/operational-signal-tags";
import styles from "./style.module.scss";

interface IProps {
  orderId: number;
  customerSatisfaction?: IOrderCustomerSatisfaction | null;
  disabled?: boolean;
  saving?: boolean;
  canViewAudits?: boolean;
  onSave: (data: IUpdateOrderCustomerSatisfactionRequest) => Promise<void>;
}

const formatUserName = (user?: any) => {
  if (!user) return "-";
  return (
    user.fullName ||
    user.name ||
    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
    `#${user.id}`
  );
};

const EMPTY_STATUS_VALUE = "empty_customer_satisfaction_status";

const CustomerSatisfactionPanel: React.FC<IProps> = ({
  orderId,
  customerSatisfaction,
  disabled,
  saving,
  canViewAudits = true,
  onSave,
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<CustomerSatisfactionStatus>();
  const [reasonId, setReasonId] = useState<number>();
  const [note, setNote] = useState("");
  const [reasons, setReasons] = useState<ICustomerDissatisfactionReason[]>([]);
  const [reasonKeyword, setReasonKeyword] = useState("");
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audits, setAudits] = useState<ICustomerSatisfactionAudit[]>([]);
  const [auditsLoading, setAuditsLoading] = useState(false);

  const activeReasonIds = useMemo(
    () => new Set(reasons.map((reason) => reason.id)),
    [reasons],
  );
  const selectedReason = useMemo(
    () => {
      if (!reasonId) return null;

      return (
        reasons.find((reason) => reason.id === reasonId) ||
        (customerSatisfaction?.reason?.id === reasonId
          ? customerSatisfaction.reason
          : null)
      );
    },
    [customerSatisfaction?.reason, reasonId, reasons],
  );
  const reasonOptions = useMemo(() => {
    const mergedReasons =
      selectedReason && !reasons.some((reason) => reason.id === selectedReason.id)
        ? [selectedReason, ...reasons]
        : reasons;

    return mergedReasons.map((reason) => ({
      label: (
        <div className={styles.reasonOption}>
          <Typography.Text className={styles.reasonOptionText}>
            {reason.reason}
          </Typography.Text>
          <OperationalSignalTags
            signals={reason.operationalSignals}
            size="small"
          />
        </div>
      ),
      title: reason.reason,
      value: reason.id,
    }));
  }, [reasons, selectedReason]);

  const isReasonRequired =
    status === CustomerSatisfactionStatus.Unsatisfied;
  const canSave =
    Boolean(status) && (!isReasonRequired || Boolean(reasonId));
  const saveTooltip = disabled
    ? t("not_allowed")
    : !status
      ? t("customer_satisfaction_status_required")
      : isReasonRequired && !reasonId
        ? t("customer_dissatisfaction_reason_required")
        : undefined;

  useEffect(() => {
    setStatus(undefined);
    setReasonId(undefined);
    setReasonKeyword("");
    setNote(customerSatisfaction?.note ?? "");
  }, [customerSatisfaction]);

  useEffect(() => {
    if (status !== CustomerSatisfactionStatus.Unsatisfied) return;

    const timeout = window.setTimeout(async () => {
      try {
        setReasonsLoading(true);
        const { data } = await EndPoints.customerDissatisfactionReason.getAll({
          page: 1,
          perPage: 1000,
          keyword: reasonKeyword || undefined,
        });
        const normalized = normalizeListingResponse(
          data,
          mapCustomerDissatisfactionReason,
        );
        setReasons(normalized.data);
      } finally {
        setReasonsLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [status, reasonKeyword]);

  useEffect(() => {
    if (
      status === CustomerSatisfactionStatus.Unsatisfied &&
      reasonId &&
      !reasonKeyword &&
      reasons.length > 0 &&
      !activeReasonIds.has(reasonId)
    ) {
      setReasonId(undefined);
    }
  }, [activeReasonIds, reasonId, reasonKeyword, reasons.length, status]);

  const handleSave = async () => {
    if (!status) {
      errorNotification(t("customer_satisfaction_status_required"));
      return;
    }

    if (status === CustomerSatisfactionStatus.Unsatisfied && !reasonId) {
      errorNotification(t("customer_dissatisfaction_reason_required"));
      return;
    }

    const payload: IUpdateOrderCustomerSatisfactionRequest = {
      status,
      reasonId:
        status === CustomerSatisfactionStatus.Unsatisfied ? reasonId : null,
      note: note.trim() || null,
    };

    await onSave(payload);
    successNotification(t("saved_successfully"));
  };

  const getAudits = async () => {
    try {
      setAuditsLoading(true);
      const { data } = await EndPoints.order.getCustomerSatisfactionAudits(
        orderId,
        { page: 1, perPage: 50 },
      );
      const normalized = normalizeListingResponse(
        data,
        mapCustomerSatisfactionAudit,
      );
      setAudits(normalized.data);
    } finally {
      setAuditsLoading(false);
    }
  };

  const renderStatusTag = (
    value?: CustomerSatisfactionStatus | number | null,
  ) => (
    <Tag
      title={getCustomerSatisfactionStatusLabel(value)}
      color={getCustomerSatisfactionStatusColor(value)}
    />
  );

  return (
    <>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Descriptions size="small" column={2} bordered>
          <AntdDescriptions.Item label={t("customer_satisfaction")}>
            {renderStatusTag(customerSatisfaction?.status)}
          </AntdDescriptions.Item>
          <AntdDescriptions.Item label={t("determined_by")}>
            {formatUserName(customerSatisfaction?.determinedBy)}
          </AntdDescriptions.Item>
          <AntdDescriptions.Item label={t("reason")}>
            {customerSatisfaction?.reason?.reason ?? "-"}
          </AntdDescriptions.Item>
          <AntdDescriptions.Item label={t("operational_signals")}>
            <OperationalSignalTags
              signals={customerSatisfaction?.reason?.operationalSignals}
              size="small"
            />
          </AntdDescriptions.Item>
          <AntdDescriptions.Item label={t("determined_at")}>
            {customerSatisfaction?.determinedAt
              ? moment(customerSatisfaction.determinedAt).format(
                  "YYYY-MM-DD HH:mm:ss",
                )
              : "-"}
          </AntdDescriptions.Item>
          <AntdDescriptions.Item label={t("note")} span={2}>
            {customerSatisfaction?.note ?? "-"}
          </AntdDescriptions.Item>
        </Descriptions>

        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <Typography.Text strong>
              {t("customer_satisfaction_status")}
            </Typography.Text>
            <div style={{ marginTop: 8 }}>
              <Segmented
                disabled={disabled}
                value={status ?? EMPTY_STATUS_VALUE}
                options={[
                  {
                    label: t("satisfied"),
                    value: CustomerSatisfactionStatus.Satisfied,
                  },
                  {
                    label: t("unsatisfied"),
                    value: CustomerSatisfactionStatus.Unsatisfied,
                  },
                  {
                    label: t("no_response"),
                    value: CustomerSatisfactionStatus.NoResponse,
                  },
                ]}
                onChange={(value) => {
                  const nextStatus = value as CustomerSatisfactionStatus;
                  setStatus(nextStatus);
                  if (nextStatus !== CustomerSatisfactionStatus.Unsatisfied) {
                    setReasonId(undefined);
                  }
                }}
              />
            </div>
          </Col>

          {status === CustomerSatisfactionStatus.Unsatisfied && (
            <Col xs={24} md={12}>
              <Typography.Text strong>
                {t("customer_dissatisfaction_reason")}
              </Typography.Text>
              <Select
                className={styles.reasonSelect}
                popupClassName={styles.reasonDropdown}
                disabled={disabled}
                loading={reasonsLoading}
                value={reasonId}
                style={{ width: "100%", marginTop: 8 }}
                placeholder={t("customer_dissatisfaction_reason")}
                options={reasonOptions}
                optionLabelProp="title"
                showSearch
                allowClear
                filterOption={false}
                onSearch={setReasonKeyword}
                onChange={(value) => setReasonId(value)}
              />
              {selectedReason && (
                <div style={{ marginTop: 8 }}>
                  <OperationalSignalTags
                    signals={selectedReason.operationalSignals}
                    size="small"
                  />
                </div>
              )}
            </Col>
          )}

          <Col span={24}>
            <Typography.Text strong>{t("note")}</Typography.Text>
            <Input.TextArea
              disabled={disabled}
              value={note}
              rows={4}
              maxLength={1000}
              showCount
              style={{ marginTop: 8 }}
              placeholder={t("note")}
              onChange={(event) => setNote(event.target.value)}
            />
          </Col>
        </Row>

        <Space>
          <Tooltip title={saveTooltip}>
            <span>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                disabled={disabled || !canSave}
                onClick={handleSave}
              >
                {t("save")}
              </Button>
            </span>
          </Tooltip>
          {canViewAudits && (
            <Button
              icon={<HistoryOutlined />}
              onClick={async () => {
                setAuditOpen(true);
                await getAudits();
              }}
            >
              {t("audit_log")}
            </Button>
          )}
        </Space>
      </Space>

      <Drawer
        title={t("customer_satisfaction_audit_log")}
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        width={900}
        destroyOnClose
      >
        <AuditChangesList audits={audits} loading={auditsLoading} showOrder />
      </Drawer>
    </>
  );
};

export default CustomerSatisfactionPanel;
